import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import AdminNavbar from "./AdminNavbar";

const API_URL = process.env.REACT_APP_API_URL;
const MAX_IMAGES = 12;

/**
 * Admin CRUD for the Services-page gallery (max 12 images).
 * Uploads go straight to Cloudinary via the backend; each upload appends
 * one image to the Mongo collection. Deletes remove a single entry.
 * The public-facing Services page pads any empty slots with placeholders.
 */
export default function GalleryDashboard() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gallery`);
      const data = await res.json();
      setImages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const data = new FormData();
      data.append("image", file);
      const res = await fetch(`${API_URL}/api/gallery`, {
        method: "POST",
        body: data,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Upload failed");
      setFile(null);
      await load();
      Swal.fire({
        icon: "success",
        title: "Uploaded.",
        confirmButtonColor: "#DC2626",
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Upload failed",
        text: err.message || "Please try again.",
        confirmButtonColor: "#DC2626",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Remove this image?",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#DC2626",
    });
    if (!ok.isConfirmed) return;
    try {
      const res = await fetch(`${API_URL}/api/gallery/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error();
      setImages(images.filter((i) => i._id !== id));
      Swal.fire({
        icon: "success",
        title: "Deleted.",
        confirmButtonColor: "#DC2626",
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        confirmButtonColor: "#DC2626",
      });
    }
  };

  const atCapacity = images.length >= MAX_IMAGES;

  return (
    <div className="admin-shell">
      <AdminNavbar />

      <div className="admin-page">
        <div className="admin-head">
          <div>
            <p className="eyebrow">Services Page</p>
            <h1>
              Manage <em>gallery</em>.
            </h1>
          </div>
        </div>

        <form
          className="admin-form"
          onSubmit={handleAdd}
          style={{ marginBottom: "3rem" }}
        >
          <div className="field">
            <label>Image ({images.length}/{MAX_IMAGES})</label>
            <label className="file-drop">
              {file ? (
                <>
                  <strong>{file.name}</strong> — click to replace
                </>
              ) : (
                <>
                  Drop or <strong>choose an image</strong>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                disabled={atCapacity}
              />
            </label>
            <small style={{ color: "var(--muted)", marginTop: "0.4rem" }}>
              {atCapacity
                ? "Gallery is full. Delete an image to add another."
                : "Images upload straight to Cloudinary."}
            </small>
          </div>
          <button
            type="submit"
            className="btn-save"
            disabled={busy || !file || atCapacity}
          >
            {busy ? "Uploading..." : "Upload Image →"}
          </button>
        </form>

        {loading ? (
          <div className="loader-wrap">
            <div className="loader" />
          </div>
        ) : images.length === 0 ? (
          <p className="empty-state">No gallery images yet.</p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "1rem",
            }}
          >
            {images.map((img) => (
              <div
                key={img._id}
                style={{
                  position: "relative",
                  border: "1px solid var(--line)",
                  borderRadius: 6,
                  overflow: "hidden",
                  aspectRatio: "1 / 1",
                  background: "var(--bg-alt, #f6f6f6)",
                }}
              >
                <img
                  src={img.url}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(img._id)}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
